import React, { useMemo } from "react";
import { FieldValues, UseControllerProps } from "react-hook-form";

import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { createFilterOptions } from "@mui/material/Autocomplete";
import Checkbox from "@mui/material/Checkbox";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import type { SxProps } from "@mui/material/styles";

import { useAppSelector } from "app/hooks";
import AutocompleteFormField from "components/autocomplete-form-field";
import {
  selectCurrentClinic,
  selectCurrentRegion,
  selectCurrentUser,
  selectHasRegions
} from "features/auth/auth-slice";
import { useGetClinicsQuery } from "services/clinics";
import { Clinic } from "services/types/clinics.type";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const CLINIC_FILTER_QUERY =
  "{id,name,logo,display_name,extra{billng_street_address_1,billng_street_address_2,billng_city,billng_state,billng_zip,billng_phone},street_address_1,street_address_2,city,state,zip_code,phone,region{id},practice{id,logo,name}}";

export type ClinicOption = {
  label: string;
  value: string;
  region: number | undefined;
};

type SelectClinicProps = {
  label?: string;
  onOptionSelected?: (
    option: ClinicOption | ClinicOption[] | null,
    clinics: Clinic[]
  ) => void;
  required?: boolean;
  multiple?: boolean;
  openOnFocus?: boolean;
  autoFocus?: boolean;
  enablePortal?: boolean;
  filterByRegions?: number[];
  skipFilteringByUser?: boolean;
  showAllRegions?: boolean;
  sx?: SxProps;
};

export type RhfSelectClinic<T extends FieldValues> = SelectClinicProps &
  UseControllerProps<T>;

export default function SelectClinic<T extends FieldValues>({
  control,
  label = "By Clinic",
  name,
  onOptionSelected,
  required = false,
  multiple = false,
  openOnFocus = false,
  enablePortal = false,
  autoFocus,
  filterByRegions,
  skipFilteringByUser,
  showAllRegions = false,
  sx
}: RhfSelectClinic<T>) {
  const currentRegion = useAppSelector(selectCurrentRegion);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const hasRegions = useAppSelector(selectHasRegions);
  const currentUser = useAppSelector(selectCurrentUser);

  const isShowOwnClinicsOnlyEnabled = currentUser.show_own_clinics_only;

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: { label: string; value: string }) => option.label
  });

  const { data: clinics = [], isLoading } = useGetClinicsQuery(
    {
      query: CLINIC_FILTER_QUERY,
      region: showAllRegions
        ? undefined
        : (filterByRegions ?? currentRegion?.id),
      user: isShowOwnClinicsOnlyEnabled
        ? currentUser?.id
        : showAllRegions
          ? undefined
          : !skipFilteringByUser && currentRegion
            ? currentUser?.id
            : undefined
    },
    {
      skip:
        !currentUser?.id &&
        !currentClinic?.id &&
        hasRegions &&
        !currentRegion?.id
    }
  );

  const options = useMemo(() => {
    return clinics.map((clinic) => ({
      label: clinic.name,
      value: String(clinic.id),
      region: clinic.region?.id
    }));
  }, [clinics]);

  return (
    <AutocompleteFormField
      enablePortal={enablePortal}
      openOnFocus={openOnFocus}
      autoHighlight
      autoFocus={autoFocus}
      name={name}
      multiple={multiple}
      label={label}
      isLoading={isLoading}
      required={required}
      filterOptions={filterOptions}
      options={options}
      getOptionLabel={(option) => {
        return option?.label ?? "";
      }}
      renderOption={(props: object, option: any, { selected }: any) => (
        <ListItem {...props}>
          {multiple && (
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selected}
            />
          )}
          <ListItemText>{option?.label}</ListItemText>
        </ListItem>
      )}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      popupIcon={<ArrowDropDownIcon />}
      control={control}
      onOptionSelected={(option) => {
        if (onOptionSelected) {
          requestAnimationFrame(() => {
            onOptionSelected(option, clinics);
          });
        }
      }}
      sx={{ ml: 0.5, mt: 0, minWidth: 220, ...sx }}
    />
  );
}
